import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { Icon, Notification } from "pepsico-ds";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import FileManagerFilters from "./components/FileManagerFilters";
import FileManagerTable from "./components/FileManagerTable";
import FileManagerTabs from "./components/FileManagerTabs";
import "./FileManagerModal.scss";
import { useFileManagerModal } from "./hooks/useFileManagerModal";
import { useFilePolling } from "./hooks/useFilePolling";
import { useGenerateDownloadLink } from "./hooks/useGenerateDownloadLink";
import {
  CONTEXT_TYPE_EXPORTS,
  CONTEXT_TYPE_IMPORTS,
  OPERATION_TYPE,
} from "./utils/fileManagerConstants";

const getInitialFilters = (context, activeTab) => {
  if (!context) {
    return {
      status: "",
      context: "",
      fromDate: "",
      toDate: "",
    };
  }
  const contextTypes =
    activeTab === "imports" ? CONTEXT_TYPE_IMPORTS : CONTEXT_TYPE_EXPORTS;
  const matchedContext = contextTypes.find((ctx) => ctx.key === context);
  let selectedContext = [];

  if (matchedContext) {
    selectedContext = [
      {
        id: matchedContext.key,
        displayText: matchedContext.text,
      },
    ];
  }
  return {
    status: "",
    context: selectedContext,
    fromDate: "",
    toDate: "",
  };
};

/**
 * FileManagerModal - Reusable modal component for viewing file export/import history
 *
 * @param {Object} props - Component props
 * @param {string} props.entityId - UUID of the calling entity
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {string} props.operationType - Operation type filter: "EXPORT" | "IMPORT" | "ALL" (default: "ALL")
 */
const FileManagerModal = ({
  isOpen,
  onClose,
  operationType = OPERATION_TYPE.ALL,
  contentType = "text/csv",
  programTimezone = "America/Chicago",
  context,
  defaultTab = "exports",
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [notification, setNotification] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [filterTrigger, setFilterTrigger] = useState(0);
  const filterRef = useRef(getInitialFilters(context, defaultTab));

  // Fetch files data
  const {
    files,
    loading,
    error,
    page,
    totalPages,
    setPage,
    refetch,
    exportCount,
    importCount,
    resetPagination,
  } = useFileManagerModal({
    operationType,
    isOpen,
    contentType,
    activeTab,
    filters: filterRef.current,
  });

  // Polling for in proccess files
  const { isPolling } = useFilePolling({ files, isOpen, refetch });

  // Download link generation
  const {
    generateLink,
    loadingFileId,
    setError: setGenerateLinkError,
  } = useGenerateDownloadLink();

  // Files are already filtered by the query based on active tab
  const displayedFiles = files;

  const handleDownload = async (fileId) => {
    const result = await generateLink(fileId);

    if (!result.success) {
      setNotification({
        type: "error",
        title: "Download Failed",
        text: result.error || "Failed to download file. Please try again.",
      });
    }
  };

  const handleTabChange = (newTab) => {
    filterRef.current = getInitialFilters(null);
    setActiveTab(newTab);
  };

  const handleFilterChange = () => {
    resetPagination();
    setFilterTrigger((prev) => prev + 1);
  };

  const handleClose = () => {
    setNotification(null);
    setGenerateLinkError(null);
    setFilterTrigger(0);
    filterRef.current = getInitialFilters(null);
    setActiveTab(defaultTab);
    onClose();
  };

  if (error && !loading && !notification) {
    setNotification({
      type: "error",
      title: "Error Loading Files",
      text: "Failed to load file history. Please try again.",
    });
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      className="file-manager-modal"
      aria-labelledby="file-manager-title"
      aria-describedby="file-manager-description"
      scroll="paper"
    >
      <DialogTitle id="file-manager-title">
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs={10}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <h2 style={{ margin: 0 }}>File History</h2>
              <Icon
                icon="refresh"
                size="medium"
                alt="reload"
                className="cursor-pointer mr-2"
                style={{ color: "#075985" }}
                onClick={() => refetch()}
              />

              {(loading || isPolling) && (
                <CircularProgress
                  size={20}
                  className="polling-indicator"
                  aria-label="Refreshing file status"
                />
              )}
            </div>
          </Grid>
          <Grid item xs={2} display="flex" justifyContent="flex-end">
            <CloseIcon
              onClick={handleClose}
              className="cursor-pointer"
              data-testid="close-modal"
              sx={{
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.7,
                },
              }}
              aria-label="Close modal"
            />
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent
        sx={{
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          padding: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={{ paddingX: 2 }}>
          {notification && (
            <div style={{ marginBottom: "16px" }}>
              <Notification
                type={notification.type}
                title={notification.title}
                text={notification.text}
                primaryActionLabel=""
                primaryActionOnClick={() => setNotification(null)}
              />
            </div>
          )}

          <FileManagerTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            exportCount={exportCount}
            importCount={importCount}
          />

          <FileManagerFilters
            filterRef={filterRef}
            onChangeFilters={handleFilterChange}
            programTimezone={programTimezone}
            activeTab={activeTab}
          />
        </Box>

        <FileManagerTable
          files={displayedFiles}
          loading={loading}
          onDownload={handleDownload}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          operationType={
            activeTab === "exports" ? OPERATION_TYPE.EXPORT : OPERATION_TYPE.IMPORT
          }
          loadingFileId={loadingFileId}
          programTimezone={programTimezone}
        />
      </DialogContent>
    </Dialog>
  );
};

FileManagerModal.propTypes = {
  entityId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  operationType: PropTypes.oneOf(["EXPORT", "IMPORT", "ALL"]),
  programTimezone: PropTypes.string,
  context: PropTypes.string,
  defaultTab: PropTypes.oneOf(["exports", "imports"]),
};

export default FileManagerModal;
